import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { FormBuilder } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import {
  AbstractDialog,
  AbstractDialogData,
  AbstractDialogResultData,
} from '@next/core-lib/dialog';
import { LocaleService } from '@next/core-lib/i18n';
import { select, Store } from '@ngrx/store';
import { BehaviorSubject, Observable, Subscription, combineLatest } from 'rxjs';
import { distinctUntilChanged, map, startWith, take } from 'rxjs/operators';

import { FmsDiagnoseCropFieldsState } from './fms-diagnose-crop-fields-tab.component';
import {
  FmsDiagnoseSearchForm,
  FmsDiagnoseSearchState,
  FmsDiagnoseSummaryState,
} from './fms-diagnose-linkage-tab.component';
import { FmsDiagnoseSpraysState } from './fms-diagnose-sprays-tab.component';
import {
  FmsDiagnoseProvider,
  FmsDiagnoseProviderCustomer,
  FmsDiagnoseCropFieldsResponse,
} from '../../../shared/models/fms-diagnose.model';
import { FmsDiagnoseApiService } from '../../../shared/services/fms-diagnose-api.service';
import { FmsSyncNotificationService } from '../../../shared/services/fms-sync-notification.service';
import { selectSessionCustomerId } from '../../../store/selectors';

export type FmsDiagnoseDialogData = AbstractDialogData;

@Component({
  selector: 'app-fms-diagnose-dialog',
  templateUrl: './fms-diagnose-dialog.component.html',
  styleUrls: ['./fms-diagnose-dialog.component.scss'],
  standalone: false,
})
export class FmsDiagnoseDialogComponent extends AbstractDialog implements OnInit, OnDestroy {
  readonly data = inject<FmsDiagnoseDialogData>(MAT_DIALOG_DATA);
  protected readonly mdDialogRef =
    inject<MatDialogRef<FmsDiagnoseDialogComponent, AbstractDialogResultData<never>>>(MatDialogRef);

  private readonly fb = inject(FormBuilder);
  private readonly locale = inject(LocaleService);
  private readonly apiService = inject(FmsDiagnoseApiService);
  private readonly fmsSyncNotifications = inject(FmsSyncNotificationService);
  private readonly store = inject(Store);
  private readonly subscriptions = new Subscription();
  private summarySubscription?: Subscription;
  private searchSubscription?: Subscription;
  private cropFieldsSubscription?: Subscription;
  private spraysSubscription?: Subscription;
  private readonly summaryStateSubject = new BehaviorSubject<FmsDiagnoseSummaryState>({
    kind: 'loading',
  });
  private readonly cropFieldsStateSubject = new BehaviorSubject<FmsDiagnoseCropFieldsState>({
    kind: 'idle',
  });
  private readonly searchStateSubject = new BehaviorSubject<FmsDiagnoseSearchState>({
    kind: 'idle',
  });
  private readonly spraysStateSubject = new BehaviorSubject<FmsDiagnoseSpraysState>({
    kind: 'idle',
  });

  readonly summaryState$ = this.summaryStateSubject.asObservable();
  readonly cropFieldsState$ = this.cropFieldsStateSubject.asObservable();
  readonly searchState$ = this.searchStateSubject.asObservable();
  readonly spraysState$ = this.spraysStateSubject.asObservable();
  readonly years = getDiagnoseYears();
  selectedTabIndex = 0;
  readonly searchForm: FmsDiagnoseSearchForm = this.fb.group({
    provider: this.fb.control<FmsDiagnoseProvider | null>(null),
    year: this.fb.nonNullable.control(getDefaultDiagnoseYear()),
    customerName: this.fb.nonNullable.control(''),
  });
  readonly filteredCustomers$: Observable<FmsDiagnoseProviderCustomer[]> = combineLatest([
    this.searchForm.controls.customerName.valueChanges.pipe(startWith('')),
    this.searchState$.pipe(map((state) => (state.kind === 'loaded' ? state.customers : []))),
  ]).pipe(
    map(([customerName, customers]) => {
      const query = customerName.trim().toLowerCase();
      return query
        ? customers.filter(
            (customer) =>
              this.providerCustomerName(customer).toLowerCase().includes(query) ||
              customer.externalFarmId.toLowerCase().includes(query),
          )
        : customers;
    }),
  );

  ngOnInit(): void {
    this.loadDiagnose();
    this.loadCropFields();
    this.loadSprays();

    this.subscriptions.add(
      this.store.pipe(select(selectSessionCustomerId), take(1)).subscribe((customerId) => {
        if (customerId === null) {
          return;
        }

        this.subscriptions.add(
          this.fmsSyncNotifications.eventsForCustomer(customerId).subscribe((event) => {
            if (event.SyncStatus === 'Syncing' || event.SyncStatus === 'Initiated') {
              this.cropFieldsStateSubject.next({ kind: 'syncing' });
              this.spraysStateSubject.next({ kind: 'syncing' });
            } else if (
              event.SyncStatus === 'Synced' ||
              event.SyncStatus === 'Failed' ||
              event.SyncStatus === 'NothingToSync'
            ) {
              this.loadCropFields();
              this.loadSprays();
            }
          }),
        );
      }),
    );

    this.subscriptions.add(
      combineLatest([
        this.searchForm.controls.provider.valueChanges.pipe(
          startWith(this.searchForm.controls.provider.value),
          distinctUntilChanged(),
        ),
        this.searchForm.controls.year.valueChanges.pipe(
          startWith(this.searchForm.controls.year.value),
          distinctUntilChanged(),
        ),
      ]).subscribe(([provider, year]) => {
        if (provider) {
          this.loadProviderCustomers(provider, year);
        } else {
          this.searchSubscription?.unsubscribe();
          this.searchStateSubject.next({ kind: 'idle' });
        }
      }),
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
    this.summarySubscription?.unsubscribe();
    this.searchSubscription?.unsubscribe();
    this.cropFieldsSubscription?.unsubscribe();
    this.spraysSubscription?.unsubscribe();
  }

  retry(): void {
    this.loadDiagnose();
  }

  retrySearch(): void {
    const provider = this.searchForm.controls.provider.value;
    const year = this.searchForm.controls.year.value;
    if (provider) {
      this.loadProviderCustomers(provider, year);
    }
  }

  onTabChange(index: number): void {
    this.selectedTabIndex = index;
    if (index === 1) {
      this.loadCropFields();
    }
    if (index === 2) {
      this.loadSprays();
    }
  }

  retryCropFields(): void {
    if (this.cropFieldsStateSubject.value.kind === 'syncing') {
      return;
    }

    this.cropFieldsStateSubject.next({ kind: 'syncing' });
    this.apiService
      .triggerFmsSync()
      .pipe(take(1))
      .subscribe({
        error: () => this.cropFieldsStateSubject.next({ kind: 'error' }),
      });
  }

  retrySprays(): void {
    if (this.spraysStateSubject.value.kind === 'syncing') {
      return;
    }

    this.spraysStateSubject.next({ kind: 'syncing' });
    this.apiService
      .triggerFmsSync()
      .pipe(take(1))
      .subscribe({
        error: () => this.spraysStateSubject.next({ kind: 'error' }),
      });
  }

  providerCustomerName(customer: FmsDiagnoseProviderCustomer): string {
    return (
      customer.customerName.trim() ||
      this.locale.translate('fmsDiagnose.tabs.linkage.farmNameUnavailable')
    );
  }

  private loadDiagnose(): void {
    this.summarySubscription?.unsubscribe();
    this.summaryStateSubject.next({ kind: 'loading' });
    this.summarySubscription = this.apiService
      .getDiagnose()
      .pipe(take(1))
      .subscribe({
        next: (data) => this.summaryStateSubject.next({ kind: 'loaded', data }),
        error: () => this.summaryStateSubject.next({ kind: 'error' }),
      });
  }

  private loadProviderCustomers(provider: FmsDiagnoseProvider, year: number): void {
    this.searchSubscription?.unsubscribe();
    this.searchStateSubject.next({ kind: 'loading' });
    this.searchSubscription = this.apiService
      .getProviderCustomers(provider, year)
      .pipe(take(1))
      .subscribe({
        next: (customers) =>
          this.searchStateSubject.next(
            customers.length > 0 ? { kind: 'loaded', customers } : { kind: 'empty' },
          ),
        error: () => this.searchStateSubject.next({ kind: 'error' }),
      });
  }

  private loadCropFields(): void {
    this.cropFieldsSubscription?.unsubscribe();
    this.cropFieldsStateSubject.next({ kind: 'loading' });
    this.cropFieldsSubscription = this.apiService
      .getCropFields()
      .pipe(take(1))
      .subscribe({
        next: (data) => this.setCropFieldsState(data),
        error: () => this.cropFieldsStateSubject.next({ kind: 'error' }),
      });
  }

  private loadSprays(): void {
    this.spraysSubscription?.unsubscribe();
    this.spraysStateSubject.next({ kind: 'loading' });
    this.spraysSubscription = this.apiService
      .getSprays()
      .pipe(take(1))
      .subscribe({
        next: (data) => this.spraysStateSubject.next({ kind: 'loaded', data }),
        error: () => this.spraysStateSubject.next({ kind: 'error' }),
      });
  }

  private setCropFieldsState(data: FmsDiagnoseCropFieldsResponse): void {
    switch (data.state) {
      case 'Unavailable':
        this.cropFieldsStateSubject.next({ kind: 'unavailable' });
        break;
      case 'Ready':
      case 'ReadyWithWarning':
        this.cropFieldsStateSubject.next({ kind: 'loaded', data });
        break;
      case 'SynchronizationFailed':
        this.cropFieldsStateSubject.next({ kind: 'error' });
        break;
      case 'Synchronizing':
        this.cropFieldsStateSubject.next({ kind: 'syncing' });
        break;
      case 'NeedsSynchronization':
        this.cropFieldsStateSubject.next({ kind: 'syncing' });
        this.apiService.triggerFmsSync().pipe(take(1)).subscribe();
        break;
    }
  }
}

export function getDiagnoseYears(now = new Date()): number[] {
  const currentYear = now.getFullYear();
  const startYear = now.getMonth() >= 9 ? currentYear - 2 : currentYear - 3;
  return Array.from({ length: 4 }, (_, index) => startYear + index).reverse();
}

export function getDefaultDiagnoseYear(now = new Date()): number {
  return now.getFullYear();
}
